import { handleShare } from "../MarketingUtils/Share";
import { NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import csrfService from "../services/csrfService";
import { detectInjection } from "../utils/injectionDetector.js";
import { frontendLogger } from '../services/frontendLogger';
import { urlConfig } from '../config/urlConfig.js';

export default function Footer() {
  const [subscription, setSubscription] = useState({
    name: '',
    email: '',
    // Honeypot fields for bot protection
    website: '',
    company: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  
  // Bot protection tracking
  const [formStartTime] = useState(Date.now());
  const [interactionCount, setInteractionCount] = useState(0);
  const [keyboardEvents, setKeyboardEvents] = useState(0);
  const [mouseEvents, setMouseEvents] = useState(0);

  // Auto-dismiss success messages after 45 seconds
  useEffect(() => {
    if (messageType === 'success' && message) {
      const timer = setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 45000); // 45 seconds
      
      return () => clearTimeout(timer);
    }
  }, [message, messageType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Security validation for newsletter inputs
    const securityCheck = validateNewsletterSecurity(name, value);
    if (!securityCheck.isClean) {
      if (securityCheck.riskLevel === 'high') {
        // Block high-risk content
        frontendLogger.warn('Newsletter field blocked - high security risk', {
          component: 'Footer',
          action: 'handleNewsletterInputChange',
          fieldName: name,
          threats: securityCheck.threats
        });
        return;
      }
    }
    
    setSubscription(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Track interactions for bot protection
    setInteractionCount(prev => prev + 1);
    
    // Clear message when user starts typing
    if (message) {
      setMessage('');
      setMessageType('');
    }
  };

  // Track keyboard events for behavioral analysis
  const handleKeyDown = () => {
    setKeyboardEvents(prev => prev + 1);
  };
  
  // Track mouse events for behavioral analysis
  const handleMouseMove = () => {
    setMouseEvents(prev => prev + 1);
  };
  
  // HTML entity decoder function
  const decodeHtmlEntities = (text) => {
    const entityMap = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#x27;': "'",
      '&#39;': "'",
      '&#x2F;': '/',
      '&#47;': '/',
      '&#x5C;': '\\',
      '&#92;': '\\',
      '&#x60;': '`',
      '&#96;': '`'
    };
    
    return text.replace(/&(?:#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (entity) => {
      if (entityMap[entity]) {
        return entityMap[entity];
      }
      // Handle numeric character references
      if (entity.startsWith('&#x')) {
        const hex = entity.slice(3, -1);
        return String.fromCharCode(parseInt(hex, 16));
      } else if (entity.startsWith('&#')) {
        const dec = entity.slice(2, -1);
        return String.fromCharCode(parseInt(dec, 10));
      }
      return entity;
    });
  };
  
  // Newsletter field security validation
  const validateNewsletterSecurity = (fieldName, text) => {
    if (!text || typeof text !== 'string') return { isClean: true };
    
    // Decode HTML entities to catch encoded attacks
    const decodedText = decodeHtmlEntities(text);
    
    // Special handling for email fields
    if (fieldName === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(decodedText.trim())) {
        // For valid email addresses, only check for script injections
        const emailThreats = [];
        const normalizedText = decodedText.toLowerCase();
        
        const emailAttackPatterns = [
          /javascript\s*:/i,
          /<script/i,
          /eval\s*\(/i,
          /\bon\w+\s*=/i,
          /<[^>]+>/i
        ];
        
        for (const pattern of emailAttackPatterns) {
          if (pattern.test(normalizedText)) {
            const match = normalizedText.match(pattern);
            emailThreats.push({
              type: 'email_injection',
              match: match[0],
              severity: 'high'
            });
          }
        }
        
        if (emailThreats.length > 0) {
          frontendLogger.warn('Newsletter email injection attempt detected', {
            component: 'Footer',
            action: 'validateEmail',
            fieldName: fieldName,
            threats: emailThreats
          });
          
          return {
            isClean: false,
            threats: emailThreats,
            riskLevel: 'high'
          };
        }
        
        return { isClean: true };
      }
    }
    
    // Use comprehensive injection detector for all fields
    const securityCheck = detectInjection(decodedText);
    
    if (!securityCheck.isClean) {
      frontendLogger.warn('Newsletter security threat detected', {
        component: 'Footer',
        action: 'validateNewsletterSecurity',
        fieldName: fieldName,
        threats: securityCheck.threats,
        riskLevel: securityCheck.riskLevel
      });
    }
    
    return securityCheck;
  };
  
  // Bot protection validation
  const validateHumanBehavior = () => {
    const submissionTime = Date.now();
    const timeSpent = submissionTime - formStartTime;
    
    // Check for honeypot fields
    if (subscription.website || subscription.company || subscription.phone) {
      throw new Error('Suspicious activity detected. Please contact support if you believe this is an error.');
    }
    
    // Check submission timing (too fast = bot)
    if (timeSpent < 2000) { // Less than 2 seconds for newsletter
      throw new Error('Form submitted too quickly. Please take your time.');
    }
    
    // Check for minimum interactions (more lenient for newsletter)
    if (interactionCount < 2) {
      throw new Error('Please manually type your name and email to verify you are human.');
    }
    
    // More lenient behavioral analysis for newsletter (accounts for browser autofill)
    if (keyboardEvents < 3 && mouseEvents < 2 && interactionCount < 4) {
      throw new Error('Please manually type your information rather than using autofill to verify you are human.');
    }
    
    return true;
  };

  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    // Basic validation
    if (!subscription.name.trim() || !subscription.email.trim()) {
      setMessage('Please fill in both name and email.');
      setMessageType('error');
      return;
    }
    
    // Security validation at submit time
    const nameSecurityCheck = validateNewsletterSecurity('name', subscription.name);
    const emailSecurityCheck = validateNewsletterSecurity('email', subscription.email);
    
    if (!nameSecurityCheck.isClean || !emailSecurityCheck.isClean) {
      setMessage('Your submission contains suspicious content and has been blocked for security reasons.');
      setMessageType('error');
      frontendLogger.warn('Newsletter submission blocked - security threats detected', {
        component: 'Footer', 
        action: 'handleNewsletterSubmit',
        nameThreats: nameSecurityCheck.threats || [],
        emailThreats: emailSecurityCheck.threats || []
      });
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(subscription.email)) {
      setMessage('Please enter a valid email address.');
      setMessageType('error');
      return;
    }
    
    setIsSubmitting(true);
    setMessage('');
    
    try {
      // Bot protection validation
      validateHumanBehavior();
      
      // Get CSRF token
      const csrfToken = await csrfService.getCSRFToken();
      
      // Submit subscription
      const response = await fetch(`${urlConfig.api.baseURL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({
          name: subscription.name.trim(),
          email: subscription.email.trim()
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage('Welcome to the sanctuary! You\'ve been added to our newsletter.');
        setMessageType('success');
        setSubscription({ 
          name: '', 
          email: '', 
          // Reset honeypot fields to prevent controlled/uncontrolled input error
          website: '',
          company: '',
          phone: ''
        }); // Clear form
      } else {
        setMessage(result.message || 'Something went wrong. Please try again.');
        setMessageType('error');
      }
    } catch (error) {
      frontendLogger.error('Newsletter subscription error', {
        component: 'Footer',
        action: 'handleNewsletterSubmit',
        error: error.message,
        stack: error.stack
      });
      setMessage('Unable to subscribe right now. Please try again later.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <footer className="bg-gradient-to-r from-sky-900 via-indigo-950 text-indigo-100 border-t border-white/10 py-8 mt-4 pt-2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12 text-sm">

        {/* About Andlegur */}
        <div className="sm:col-span-2 lg:col-span-1">
          <h2 className="text-indigo-50 text-base font-semibold mb-2">About Andlegur</h2>
          <p className="text-indigo-200 leading-relaxed">
           Andlegur is a supportive space for emotional well-being and self-development, helping you cultivate clarity, balance, and direction in life and work—at your own pace.
          </p>
        </div>

        {/* Navigation */}
        <div className="sm:col-span-1 lg:col-span-1">
          <h2 className="text-indigo-100 text-base font-semibold mb-2">Explore</h2>
          <ul className="space-y-2">
            <li>
              <NavLink to="/get-started" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2 group hover:text-sky-300 transition-colors">
                <span className="w-2 h-2 rounded-full bg-gradient-to-r from-teal-400 to-sky-400 group-hover:scale-125 group-hover:from-teal-300 group-hover:to-sky-300 transition-transform"></span>
                Get Started
              </NavLink>
            </li>
            <li>
              <NavLink to="/contact-us" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2 group hover:text-sky-300 transition-colors">
                <span className="w-2 h-2 rounded-full bg-gradient-to-r from-teal-400 to-sky-400 group-hover:scale-125 group-hover:from-teal-300 group-hover:to-sky-300 transition-transform"></span>
                Contact Us
              </NavLink>
            </li>
            <li>
              <NavLink to="/community" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2 group hover:text-sky-300 transition-colors">
                <span className="w-2 h-2 rounded-full bg-gradient-to-r from-teal-400 to-sky-400 group-hover:scale-125 group-hover:from-teal-300 group-hover:to-sky-300 transition-transform"></span>
                Community
              </NavLink>
            </li>
            <li>
              <NavLink to="/privacy" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2 group hover:text-sky-300 transition-colors">
                <span className="w-2 h-2 rounded-full bg-gradient-to-r from-teal-400 to-sky-400 group-hover:scale-125 group-hover:from-teal-300 group-hover:to-sky-300 transition-transform"></span>
                Privacy Policy
              </NavLink>
            </li>
            <li>
              <NavLink to="/termsofservice" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2 group hover:text-sky-300 transition-colors">
                <span className="w-2 h-2 rounded-full bg-gradient-to-r from-teal-400 to-sky-400 group-hover:scale-125 group-hover:from-teal-300 group-hover:to-sky-300 transition-transform"></span>
                Terms of Service
              </NavLink>
            </li>
            <li>
              <NavLink to="/dpa-summary" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2 group hover:text-sky-300 transition-colors">
                <span className="w-2 h-2 rounded-full bg-gradient-to-r from-teal-400 to-sky-400 group-hover:scale-125 group-hover:from-teal-300 group-hover:to-sky-300 transition-transform"></span>
                DPA Summary
              </NavLink>
            </li>
          </ul>
        </div>

        {/* Sharing Section */}
        <div className="sm:col-span-1 lg:col-span-1">
          <h2 className="text-indigo-50 text-base font-semibold mb-2">Together We Rise</h2>
          <p className="text-indigo-200 mb-4">Help expand the sanctuary—invite someone to join the space.</p>
          <div className="flex flex-wrap gap-3 mt-2">
            {/* Native share */}
            <button
              onClick={() => handleShare(window.location.origin + '/')}
              className="bg-indigo-600 hover:bg-rose-500 transition-colors p-2 z-10 rounded-full shadow text-white flex items-center justify-center"
              aria-label="Share via device"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <circle cx="18" cy="5" r="2" />
                <circle cx="6" cy="12" r="2" />
                <circle cx="18" cy="19" r="2" />
                <line x1="7.91" y1="10.59" x2="16.09" y2="6.41" stroke="currentColor" strokeWidth="1.5" />
                <line x1="7.91" y1="13.41" x2="16.09" y2="17.59" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </button>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/andlegur1/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Andlegur on Instagram"
              className="bg-indigo-600 hover:bg-pink-500 transition-colors p-2 rounded-full shadow text-white flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.975.975 1.246 2.242 1.308 3.608.058 1.266.07 1.646.07 4.851s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.975.975-2.242 1.246-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.975-.975-1.246-2.242-1.308-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.975-.975 2.242-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.333.014 7.053.072 5.775.13 4.602.402 3.635 1.368 2.668 2.335 2.396 3.508 2.338 4.786.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.058 1.278.33 2.451 1.297 3.418.967.967 2.14 1.239 3.418 1.297C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 1.278-.058 2.451-.33 3.418-1.297.967-.967 1.239-2.14 1.297-3.418.058-1.28.072-1.689.072-4.948 0-3.259-.014-3.667-.072-4.947-.058-1.278-.33-2.451-1.297-3.418C20.399.402 19.226.13 17.948.072 16.668.014 16.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zm0 10.162a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </a>

            {/* Twitter / X */}
            <a
              href="https://x.com/andlegur"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Andlegur on X (Twitter)"
              className="bg-indigo-600 hover:bg-sky-400 transition-colors p-2 rounded-full shadow text-white flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.26 5.632 5.904-5.632zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>

            {/* YouTube */}
            <a
              href="https://www.youtube.com/@Andlegur"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Watch Andlegur on YouTube"
              className="bg-indigo-600 hover:bg-red-500 transition-colors p-2 rounded-full shadow text-white flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </a>

            {/* Facebook */}
            <a
              href="https://www.facebook.com/profile.php?id=61563751566301"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Andlegur on Facebook"
              className="bg-indigo-600 hover:bg-blue-500 transition-colors p-2 rounded-full shadow text-white flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.791-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.269h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
              </svg>
            </a>
          </div>
        </div>

        {/* Stay Connected */}
        <div className="sm:col-span-2 lg:col-span-1">
          <h2 className="text-indigo-50 text-base font-semibold mb-2">Stay Connected</h2>
          <p className="text-indigo-200 mb-4">Subscribe for reflections, updates, and new experiences from Andlegur.</p>
          
          <form 
            onSubmit={handleSubscriptionSubmit} 
            className="flex flex-col gap-2 w-full max-w-sm"
            onKeyDown={handleKeyDown}
            onMouseMove={handleMouseMove}
          >
            <input
              type="text"
              name="name"
              placeholder="Your name"
              value={subscription.name}
              onChange={handleInputChange}
              disabled={isSubmitting}
              maxLength="50"
              className="px-4 py-2 rounded bg-indigo-800 text-white placeholder-indigo-200 border border-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 w-full"
              required
            />
            <input
              type="email"
              name="email"
              placeholder="Your email"
              value={subscription.email}
              onChange={handleInputChange}
              disabled={isSubmitting}
              maxLength="120"
              className="px-4 py-2 rounded bg-indigo-800 text-white placeholder-indigo-200 border border-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 w-full"
              required
            />
            
            {/* Honeypot fields - hidden from humans, visible to bots */}
            <div style={{ display: 'none' }}>
              <input
                type="text"
                name="website"
                value={subscription.website}
                onChange={handleInputChange}
                tabIndex="-1"
                autoComplete="off"
              />
              <input
                type="text"
                name="company"
                value={subscription.company}
                onChange={handleInputChange}
                tabIndex="-1"
                autoComplete="off"
              />
              <input
                type="tel"
                name="phone"
                value={subscription.phone}
                onChange={handleInputChange}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-700 disabled:opacity-60 transition-colors px-4 py-2 rounded text-white font-semibold flex items-center justify-center gap-2 w-full"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                  </svg>
                  Joining...
                </>
              ) : (
                'Join the Pulse'
              )}
            </button>
            
            {message && (
              <div className={`text-sm mt-2 px-3 py-2 rounded ${
                messageType === 'success' 
                  ? 'bg-green-900/50 text-green-200 border border-green-700/50' 
                  : 'bg-red-900/50 text-red-200 border border-red-700/50'
              }`}>
                {message}
              </div>
            )}
          </form>
        </div>
        </div>
      </div>

      <div className="mt-10 text-left text-base text-sky-100 max-w-7xl mx-auto px-4 sm:px-6">
        <span className="inline-block bg-black/60 px-4 py-2 rounded-lg shadow">
          © {new Date().getFullYear()} Andlegur — All rights reserved. Created with care, intention, and clarity.
        </span>
      </div>
    </footer>
  );
}
